import React, { Fragment, useEffect, useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import moment from 'moment';
import { EiqPagination } from 'eiq-storybook';
import { toSimpleUtcDateString } from 'src/utils/format/date';
import ReactTooltip from 'react-tooltip';
import Loader from 'src/components/Loader';
import { PieChart } from 'react-minimal-pie-chart';
import { makeJobUrl } from 'src/routes/routes';
import { useData } from './hooks';
import s from '../Dashboards.styles';

export const AuditorDashboardWidget = () => {
  const {
    auditJobs,
    totalAuditJobs,
    reFetchNextAuditJobBatch,
    reviewJobs,
    totalReviewJobs,
    reFetchNextReviewJobBatch,
    auditJobsLoading,
    reviewJobsLoading,
  } = useData();

  const [auditJobsOffsetMovedBy, setAuditJobsOffsetMovedBy] = useState(0);
  const [auditJobPageCounter, setAuditJobPageCounter] = useState(1);

  const [reviewJobsOffsetMovedBy, setReviewJobsOffsetMovedBy] = useState(0);
  const [reviewJobPageCounter, setReviewJobPageCounter] = useState(1);

  const { t } = useTranslation();

  const auditJobsPrevInactive =
    auditJobsOffsetMovedBy === 0 || totalAuditJobs === 0;
  const auditJobsNextInactive =
    auditJobsOffsetMovedBy + auditJobs.length === totalAuditJobs ||
    totalAuditJobs === 0;

  const reviewJobsPrevInactive =
    reviewJobsOffsetMovedBy === 0 || totalReviewJobs === 0;
  const reviewJobsNextInactive =
    reviewJobsOffsetMovedBy + reviewJobs.length === totalReviewJobs ||
    totalReviewJobs === 0;

  const pieChart = job => {
    const closedNoteCount = job.reportReviewClosedNotesCount;
    const allNoteCount = job.reportReviewAllNotesCount;

    return (
      <s.Status flex>
        <s.PieChartDiv
          data-for="AuditorDashboardWidget"
          data-tip={t('REPORT_REVIEWER_DASHBOARD_NOTES_CLOSED')}
        >
          <PieChart
            startAngle={-90}
            lengthAngle={360}
            data={[
              {
                color: '#15af4c',
                title: t('AUDITOR_DASHBOARD_CLOSED_NOTE_COUNT'),
                value: allNoteCount === 0 ? 0 : closedNoteCount,
              },
              {
                color: '#d9d9d9',
                title: t('AUDITOR_DASHBOARD_OPEN_NOTE_COUNT'),
                value: allNoteCount === 0 ? 1 : allNoteCount - closedNoteCount,
              },
            ]}
            viewBoxSize={[100, 100]}
            lineWidth={15}
            radius={50}
            animationDuration={500}
            segmentsStyle={{
              transition: 'stroke .3s ease',
            }}
            animate={false}
            segmentsShift={0}
            animationEasing="ease-out"
            label={() => null}
            style={{
              height: '25px',
              width: '25px',
              transform: 'rotate(-90deg)',
            }}
          />
          <s.PieChartText>
            {allNoteCount === 0 ? 0 : `${closedNoteCount}/${allNoteCount}`}
          </s.PieChartText>
        </s.PieChartDiv>
      </s.Status>
    );
  };

  return (
    <Fragment>
      <s.DashboardComponent>
        <s.DashboardComponentTitle widget>
          <Trans>AUDITOR_DASHBOARD_AUDIT_JOBS</Trans>
          <s.OrderByText>
            <Trans>AUDITOR_DASHBOARD_SORT_BY_AUDIT_START</Trans>
          </s.OrderByText>
        </s.DashboardComponentTitle>
        {auditJobsLoading ? (
          <Loader />
        ) : (
          <s.WidgetDiv>
            <ReactTooltip id="AuditorDashboardWidget" place="bottom" />
            {auditJobs.map(job => (
              <s.WidgetCard key={job.jobId}>
                <s.WidgetCardInnerDiv>
                  <s.JobName to={makeJobUrl(job.jobId)}>
                    {job.reportNumber}
                  </s.JobName>
                  <s.FactoryName>{job.factoryName}</s.FactoryName>
                  <s.CardInfoWithMargin>
                    <s.ServicesName
                      data-for="AuditorDashboardWidget"
                      data-tip={
                        job.serviceNames && job.serviceNames.length > 0
                          ? job.serviceNames.join(', ')
                          : 'N/A'
                      }
                    >
                      <s.ServiceButtonBookIcon name="serviceIconBlue" />
                      {job.serviceNames && job.serviceNames.length > 0
                        ? job.serviceNames.join(', ')
                        : 'N/A'}
                    </s.ServicesName>
                    <s.ProgramName
                      data-for="AuditorDashboardWidget"
                      data-tip={job.programName || 'N/A'}
                    >
                      <s.ServiceButtonBookIcon name="folder" />
                      {job.programName || 'N/A'}
                    </s.ProgramName>
                  </s.CardInfoWithMargin>
                  <s.DateAndAssignedTo>
                    <s.DateAndAssignedToLeft>
                      <s.CardSmallTitle>
                        <Trans>AUDITOR_DASHBOARD_AUDIT_START_END_DATE</Trans>
                      </s.CardSmallTitle>
                      <s.CardSmallValue
                        data-for="AuditorDashboardWidget"
                        data-tip={`${toSimpleUtcDateString(
                          job.auditStartAt,
                        )} / ${toSimpleUtcDateString(job.auditEndAt)}`}
                      >
                        <s.AuditStartDate>
                          {toSimpleUtcDateString(job.auditStartAt)}
                        </s.AuditStartDate>{' '}
                        / {toSimpleUtcDateString(job.auditEndAt)}
                      </s.CardSmallValue>
                    </s.DateAndAssignedToLeft>
                    <s.DateAndAssignedToRight>
                      <s.CardSmallTitle>
                        <Trans>AUDITOR_DASHBOARD_ASSIGNED_TO</Trans>
                      </s.CardSmallTitle>
                      <s.CardSmallValue
                        data-for="AuditorDashboardWidget"
                        data-tip={job.auditorNames.join(', ')}
                      >
                        {job.auditorNames.join(', ')}
                      </s.CardSmallValue>
                    </s.DateAndAssignedToRight>
                  </s.DateAndAssignedTo>
                </s.WidgetCardInnerDiv>
              </s.WidgetCard>
            ))}
            <s.CornerNavigationContainer>
              <EiqPagination
                dataCount={totalAuditJobs}
                showPerPage={false}
                pageSize={18}
                canPreviousPage={!auditJobsPrevInactive}
                canNextPage={!auditJobsNextInactive}
                gotoPage={pageIndex => {
                  const newCountOfAuditJobsOffset = pageIndex * 18;
                  setAuditJobsOffsetMovedBy(newCountOfAuditJobsOffset);
                  reFetchNextAuditJobBatch(newCountOfAuditJobsOffset);
                  setAuditJobPageCounter(pageIndex + 1);
                }}
                pageIndex={auditJobPageCounter - 1}
                nextPage={() => {
                  if (auditJobsNextInactive) {
                    return;
                  }
                  const newCountOfAuditJobsOffset = auditJobsOffsetMovedBy + 18;
                  setAuditJobsOffsetMovedBy(newCountOfAuditJobsOffset);
                  reFetchNextAuditJobBatch(newCountOfAuditJobsOffset);
                  setAuditJobPageCounter(auditJobPageCounter + 1);
                }}
                previousPage={() => {
                  if (auditJobsPrevInactive) {
                    return;
                  }
                  const newCountOfAuditJobsOffset = auditJobsOffsetMovedBy - 18;
                  setAuditJobsOffsetMovedBy(newCountOfAuditJobsOffset);
                  reFetchNextAuditJobBatch(newCountOfAuditJobsOffset);
                  setAuditJobPageCounter(auditJobPageCounter - 1);
                }}
              />
            </s.CornerNavigationContainer>
          </s.WidgetDiv>
        )}
      </s.DashboardComponent>
      <s.DashboardComponent>
        <s.DashboardComponentTitle widget>
          <Trans>AUDITOR_DASHBOARD_REVIEW_JOBS</Trans>
        </s.DashboardComponentTitle>
        {reviewJobsLoading ? (
          <Loader />
        ) : (
          <s.WidgetDiv>
            {reviewJobs.map(job => (
              <s.WidgetCard key={job.jobId}>
                <s.WidgetCardInnerDiv>
                  {pieChart(job)}
                  <s.JobName to={makeJobUrl(job.jobId)}>
                    {job.reportNumber}
                  </s.JobName>
                  <s.FactoryName>{job.factoryName}</s.FactoryName>
                  <s.JobStatusSpan underReview={true}>
                    <Trans>AUDITOR_DASHBOARD_UNDER_REVIEW</Trans>
                  </s.JobStatusSpan>
                  {job.actionRequired && (
                    <s.JobStatusSpan actionRequired={true}>
                      <Trans>AUDITOR_DASHBOARD_ACTION_REQUIRED</Trans>
                    </s.JobStatusSpan>
                  )}
                  <s.CardInfoWithMargin>
                    <s.ServicesName
                      data-for="AuditorDashboardWidget"
                      data-tip={
                        job.serviceNames && job.serviceNames.length > 0
                          ? job.serviceNames.join(', ')
                          : 'N/A'
                      }
                    >
                      <s.ServiceButtonBookIcon name="serviceIconBlue" />
                      {job.serviceNames && job.serviceNames.length > 0
                        ? job.serviceNames.join(', ')
                        : 'N/A'}
                    </s.ServicesName>
                  </s.CardInfoWithMargin>
                  <s.DateAndAssignedTo>
                    <s.DateAndAssignedToLeft>
                      <s.CardSmallTitle>
                        <Trans>DUE_DATE</Trans>
                      </s.CardSmallTitle>
                      <s.CardSmallValue
                        data-for="AuditorDashboardWidget"
                        data-tip={moment
                          .utc(job.auditEndAt)
                          .add(7, 'day')
                          .format('DD-MMM-YYYY')}
                      >
                        <s.AuditStartDate>
                          {moment
                            .utc(job.auditEndAt)
                            .add(7, 'day')
                            .format('DD-MMM-YYYY')}
                        </s.AuditStartDate>
                      </s.CardSmallValue>
                    </s.DateAndAssignedToLeft>
                    <s.DateAndAssignedToRight>
                      <s.CardSmallTitle>
                        <Trans>AUDITOR_DASHBOARD_ASSIGNED_TO</Trans>
                      </s.CardSmallTitle>
                      <s.CardSmallValue
                        data-for="AuditorDashboardWidget"
                        data-tip={job.auditorNames.join(', ')}
                      >
                        {job.auditorNames.join(', ')}
                      </s.CardSmallValue>
                    </s.DateAndAssignedToRight>
                  </s.DateAndAssignedTo>
                </s.WidgetCardInnerDiv>
              </s.WidgetCard>
            ))}
            <s.CornerNavigationContainer>
              <EiqPagination
                dataCount={totalReviewJobs}
                showPerPage={false}
                pageSize={18}
                canPreviousPage={!reviewJobsPrevInactive}
                canNextPage={!reviewJobsNextInactive}
                pageIndex={reviewJobPageCounter - 1}
                gotoPage={pageIndex => {
                  const newCountOfReviewJobsOffset = pageIndex * 18;
                  setReviewJobsOffsetMovedBy(newCountOfReviewJobsOffset);
                  reFetchNextReviewJobBatch(newCountOfReviewJobsOffset);
                  setReviewJobPageCounter(pageIndex + 1);
                }}
                nextPage={() => {
                  if (reviewJobsNextInactive) {
                    return;
                  }
                  const newCountOfReviewJobsOffset =
                    auditJobsOffsetMovedBy + 18;
                  setReviewJobsOffsetMovedBy(newCountOfReviewJobsOffset);
                  reFetchNextReviewJobBatch(newCountOfReviewJobsOffset);
                  setReviewJobPageCounter(reviewJobPageCounter + 1);
                }}
                previousPage={() => {
                  if (reviewJobsPrevInactive) {
                    return;
                  }
                  const newCountOfReviewJobsOffset =
                    reviewJobsOffsetMovedBy - 18;
                  setReviewJobsOffsetMovedBy(newCountOfReviewJobsOffset);
                  reFetchNextReviewJobBatch(newCountOfReviewJobsOffset);
                  setReviewJobPageCounter(reviewJobPageCounter - 1);
                }}
              />
            </s.CornerNavigationContainer>
          </s.WidgetDiv>
        )}
      </s.DashboardComponent>
    </Fragment>
  );
};

export default React.memo(AuditorDashboardWidget);
